import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useEffect, lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/sonner";
import { ThemeProvider } from "@/context/ThemeContext";
import { AuthProvider } from "@/context/AuthContext";
import { LocaleProvider } from "@/context/LocaleContext";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import FoundingBanner from "@/components/FoundingBanner";
import ProtectedRoute from "@/components/ProtectedRoute";
import ErrorBoundary from "@/components/ErrorBoundary";
import ZuntriaLoader from "@/components/ZuntriaLoader";
import InstallPrompt from "@/components/InstallPrompt";
import HomePage from "@/pages/HomePage";
import NotFoundPage from "@/pages/NotFoundPage";
import PaystackCallbackPage from "@/pages/PaystackCallbackPage";
import RentPaidPage from "@/pages/RentPaidPage";
import CreditsPage from "@/pages/CreditsPage";
import { RouteSeo } from "@/components/RouteSeo";
import { api } from "@/lib/api";

function VisitTracker() {
  useEffect(() => {
    try {
      const today = new Date().toISOString().slice(0, 10);
      if (localStorage.getItem("z_visit_day") === today) return;
      let vid = localStorage.getItem("z_vid");
      if (!vid) {
        vid = crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
        localStorage.setItem("z_vid", vid);
      }
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/track/visit`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ vid }),
      }).then(() => localStorage.setItem("z_visit_day", today)).catch(() => {});
    } catch {}
  }, []);
  return null;
}
import LoginPage from "@/pages/LoginPage";
import SignupPage from "@/pages/SignupPage";
import ChatPage from "@/pages/ChatPage";
import AjoPage from "@/pages/AjoPage";
import HistoryPage from "@/pages/HistoryPage";
import PricingPage from "@/pages/PricingPage";
import WalletPage from "@/pages/WalletPage";
import RewardsPage from "@/pages/RewardsPage";
import ReferralPage from "@/pages/ReferralPage";
import AutopilotPage from "@/pages/AutopilotPage";
import CvBuilderPage from "@/pages/CvBuilderPage";
import LetterBuilderPage from "@/pages/LetterBuilderPage";
import DocumentsListPage from "@/pages/DocumentsListPage";
import OnboardingPage from "@/pages/OnboardingPage";
import SettingsPage from "@/pages/SettingsPage";
import KoloPage from "@/pages/KoloPage";
import GroupsPage from "@/pages/GroupsPage";
import HustleProPage from "@/pages/HustleProPage";
import MarketplacePage from "@/pages/MarketplacePage";
import FreelancersPage from "@/pages/FreelancersPage";
import AuthCallback from "@/pages/AuthCallback";
import PrivacyPage from "@/pages/PrivacyPage";
import TermsPage from "@/pages/TermsPage";
import RefundPage from "@/pages/RefundPage";
import HelpPage from "@/pages/HelpPage";
import AdminPage from "@/pages/AdminPage";
import HealthPlanPage from "@/pages/HealthPlanPage";
import CommunityPage from "@/pages/CommunityPage";
import ChallengesPage from "@/pages/ChallengesPage";
import CreatorPage from "@/pages/CreatorPage";
import StudioPage from "@/pages/StudioPage";
import FreeRoadmapPage from "@/pages/FreeRoadmapPage";
import MoneyEnginePage from "@/pages/MoneyEnginePage";
import AboutPage from "@/pages/AboutPage";
import ContactPage from "@/pages/ContactPage";
import AssistantsPage from "@/pages/AssistantsPage";
import AnalyzePage from "@/pages/AnalyzePage";
import TemplatesPage from "@/pages/TemplatesPage";
import TrufflPage from "@/pages/TrufflPage";
import IntegrationsPage from "@/pages/IntegrationsPage";
import ApplicationsPage from "@/pages/ApplicationsPage";
import AjoSavingsPage from "@/pages/AjoSavingsPage";
import FindClientsPage from "@/pages/FindClientsPage";
import AiCvBuilderPage from "@/pages/AiCvBuilderPage";
import JobTrackerPage from "@/pages/JobTrackerPage";
import BookPage from "@/pages/BookPage";
import BlogPage from "@/pages/BlogPage";
import BlogArticlePage from "@/pages/BlogArticlePage";
import PartnersPage from "@/pages/PartnersPage";
import "@/App.css";

const DashboardPage = lazy(() => import("@/pages/DashboardPage"));
const HustlesPage = lazy(() => import("@/pages/HustlesPage"));
const DispatchPage = lazy(() => import("@/pages/DispatchPage"));

// DashboardContentContainer — dashboard code loads on demand, never with the landing page bundle
function DashboardContentContainer() {
  return (
    <Suspense fallback={<ZuntriaLoader label="Loading your dashboard…" />}>
      <DashboardPage />
    </Suspense>
  );
}

function AppShell() {
  const location = useLocation();

  // Soft-redirect from preview domain to canonical production domain.
  // Skips when ?dev=1 is in the URL so the team can still test the preview.
  useEffect(() => {
    if (typeof window === "undefined") return;
    // Sticky dev flag: visit ?dev=1 once → preview stays accessible forever in this browser
    if (new URLSearchParams(window.location.search).get("dev") === "1") {
      window.localStorage.setItem("zuntria_dev", "1");
    }
    const onPreview = window.location.hostname.includes(".preview.emergentagent.com");
    const dev = window.localStorage.getItem("zuntria_dev") === "1";
    if (onPreview && !dev) {
      // Derive production hostname by substituting the preview suffix → emergent.host
      const prodHost = window.location.hostname.replace(".preview.emergentagent.com", ".emergent.host");
      const target = `${window.location.protocol}//${prodHost}${window.location.pathname}${window.location.search}${window.location.hash}`;
      window.location.replace(target);
    }
  }, []);

  // 📈 Acquisition capture: remember how this visitor first arrived (UTM/referrer)
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (window.localStorage.getItem("zuntria_acq")) return;
    const p = new URLSearchParams(window.location.search);
    const acq = {
      utm_source: p.get("utm_source") || null,
      utm_medium: p.get("utm_medium") || null,
      utm_campaign: p.get("utm_campaign") || null,
      referrer: document.referrer || null,
      landing: window.location.pathname,
    };
    window.localStorage.setItem("zuntria_acq", JSON.stringify(acq));
  }, []);

  // 🤝 Affiliate/referral first-click capture — 30-day attribution, never overwritten while valid
  useEffect(() => {
    if (typeof window === "undefined") return;
    const code = new URLSearchParams(window.location.search).get("ref");
    if (!code) return;
    try {
      const existing = JSON.parse(window.localStorage.getItem("zuntria_ref") || "null");
      if (existing && new Date(existing.expires) > new Date()) return; // first click wins
    } catch (e) { /* recapture */ }
    const expires = new Date(Date.now() + 30 * 24 * 3600 * 1000).toISOString();
    window.localStorage.setItem("zuntria_ref", JSON.stringify({ code, expires }));
    api.post("/affiliate/click", { code }).catch(() => {});
  }, []);

  // Handle OAuth callback first (Google returns user to /dashboard#session_id=...)
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <div className="App flex min-h-screen flex-col">
      <FoundingBanner />
      <Header />
      <VisitTracker />
      <main className="flex-1">
        <RouteSeo />
        <ErrorBoundary>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/signup" element={<SignupPage />} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/free-roadmap" element={<FreeRoadmapPage />} />
          <Route path="/money" element={<ProtectedRoute><MoneyEnginePage /></ProtectedRoute>} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/assistants" element={<AssistantsPage />} />
          <Route path="/analyze" element={<ProtectedRoute><AnalyzePage /></ProtectedRoute>} />
          <Route path="/templates" element={<ProtectedRoute><TemplatesPage /></ProtectedRoute>} />
          <Route path="/dashboard" element={<ProtectedRoute><DashboardContentContainer /></ProtectedRoute>} />
          <Route path="/hustles" element={<ProtectedRoute><Suspense fallback={<ZuntriaLoader />}><HustlesPage /></Suspense></ProtectedRoute>} />
          <Route path="/truffl" element={<ProtectedRoute><TrufflPage /></ProtectedRoute>} />
          <Route path="/remittance" element={<Navigate to="/money" replace />} />
          <Route path="/dispatch" element={<ProtectedRoute><Suspense fallback={<ZuntriaLoader />}><DispatchPage /></Suspense></ProtectedRoute>} />
          <Route path="/chat/:assistantId" element={<ProtectedRoute><ChatPage /></ProtectedRoute>} />
          <Route path="/ajo" element={<ProtectedRoute><AjoPage /></ProtectedRoute>} />
          <Route path="/history" element={<ProtectedRoute><HistoryPage /></ProtectedRoute>} />
          <Route path="/wallet" element={<ProtectedRoute><WalletPage /></ProtectedRoute>} />
          <Route path="/rewards" element={<ProtectedRoute><RewardsPage /></ProtectedRoute>} />
          <Route path="/referrals" element={<ProtectedRoute><ReferralPage /></ProtectedRoute>} />
          <Route path="/autopilot" element={<ProtectedRoute><AutopilotPage /></ProtectedRoute>} />
          <Route path="/builder/cv" element={<ProtectedRoute><CvBuilderPage /></ProtectedRoute>} />
          <Route path="/builder/letter" element={<ProtectedRoute><LetterBuilderPage /></ProtectedRoute>} />
          <Route path="/documents" element={<ProtectedRoute><DocumentsListPage /></ProtectedRoute>} />
          <Route path="/onboarding" element={<ProtectedRoute><OnboardingPage /></ProtectedRoute>} />
          <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
          <Route path="/integrations" element={<ProtectedRoute><IntegrationsPage /></ProtectedRoute>} />
          <Route path="/applications" element={<ProtectedRoute><ApplicationsPage /></ProtectedRoute>} />
          <Route path="/partners" element={<ProtectedRoute><PartnersPage /></ProtectedRoute>} />
          <Route path="/kolo" element={<ProtectedRoute><KoloPage /></ProtectedRoute>} />
          <Route path="/groups" element={<ProtectedRoute><GroupsPage /></ProtectedRoute>} />
          <Route path="/groups/:groupId" element={<ProtectedRoute><GroupsPage /></ProtectedRoute>} />
          <Route path="/hustle" element={<ProtectedRoute><HustleProPage /></ProtectedRoute>} />
          <Route path="/marketplace" element={<ProtectedRoute><MarketplacePage /></ProtectedRoute>} />
          <Route path="/freelancers" element={<ProtectedRoute><FreelancersPage /></ProtectedRoute>} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/refund" element={<RefundPage />} />
          <Route path="/help" element={<HelpPage />} />
          <Route path="/ajo-savings" element={<AjoSavingsPage />} />
          <Route path="/find-clients" element={<FindClientsPage />} />
          <Route path="/ai-cv-builder" element={<AiCvBuilderPage />} />
          <Route path="/job-tracker" element={<JobTrackerPage />} />
          <Route path="/book" element={<BookPage />} />
          <Route path="/blog" element={<BlogPage />} />
          <Route path="/blog/:slug" element={<BlogArticlePage />} />
          <Route path="/admin" element={<ProtectedRoute><AdminPage /></ProtectedRoute>} />
          <Route path="/health-plan" element={<ProtectedRoute><HealthPlanPage /></ProtectedRoute>} />
          <Route path="/community" element={<ProtectedRoute><CommunityPage /></ProtectedRoute>} />
          <Route path="/challenges" element={<ProtectedRoute><ChallengesPage /></ProtectedRoute>} />
          <Route path="/creator" element={<CreatorPage />} />
          <Route path="/studio" element={<ProtectedRoute><StudioPage /></ProtectedRoute>} />
          <Route path="/payment/paystack" element={<PaystackCallbackPage />} />
          <Route path="/rent-paid" element={<RentPaidPage />} />
          <Route path="/credits" element={<ProtectedRoute><CreditsPage /></ProtectedRoute>} />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
        </ErrorBoundary>
      </main>
      <Footer />
    </div>
  );
}

export default function App() {
  return (
    <ThemeProvider>
      <BrowserRouter>
        <AuthProvider>
          <LocaleProvider>
            <ErrorBoundary>
              <AppShell />
            </ErrorBoundary>
            <InstallPrompt />
            <Toaster richColors position="top-center" />
          </LocaleProvider>
        </AuthProvider>
      </BrowserRouter>
    </ThemeProvider>
  );
}
